/**
 * Where a cycle sits relative to today.
 *
 * Observations can be dated in the future (pre-logged entries, seeded demo data), so the
 * newest derived cycle isn't necessarily the one running now: a cycle start logged for next
 * month makes a cycle that hasn't happened yet. Everything that means "the current cycle" or
 * "a completed cycle" should go through these helpers instead of `!cycle.end_date` /
 * `cycles[0]`.
 *
 * All dates are `yyyy-MM-dd` keys, so string comparison is calendar comparison. Pass the
 * owner's timezone-aware today (getDateKeyInTimeZone(new Date(), couple.timezone)) where
 * one is available.
 */

import { getDateKeyInTimeZone } from "./dateTime";

export interface TimedCycle {
  start_date: string;
  end_date?: string | null;
}

export type CycleTiming = "past" | "current" | "upcoming";

/** Today's date key in the given timezone (device timezone when omitted). */
export const getTodayKey = (timeZone?: string | null): string =>
  getDateKeyInTimeZone(new Date(), timeZone);

/**
 * - upcoming: starts after today — pre-logged data, not yet happened.
 * - past: ended before today — the only kind whose length is actually known.
 * - current: contains today (including an open-ended cycle that started on or before today).
 */
export const getCycleTiming = (cycle: TimedCycle, todayKey: string): CycleTiming => {
  if (cycle.start_date > todayKey) return "upcoming";
  if (cycle.end_date && cycle.end_date < todayKey) return "past";
  return "current";
};

/** The cycle containing today, or null when today precedes every cycle (or there are none). */
export const getCurrentCycle = <T extends TimedCycle>(
  cycles: readonly T[],
  todayKey: string
): T | null => cycles.find((cycle) => getCycleTiming(cycle, todayKey) === "current") ?? null;

/** Ended before today — safe to measure. Current and upcoming cycles are not completed. */
export const isPastCycle = (cycle: TimedCycle, todayKey: string): boolean =>
  getCycleTiming(cycle, todayKey) === "past";

export const isUpcomingCycle = (cycle: TimedCycle, todayKey: string): boolean =>
  getCycleTiming(cycle, todayKey) === "upcoming";

/** True while the cycle is still running as of today (no end, or an end not yet reached). */
export const isCycleOngoing = (cycle: TimedCycle, todayKey: string): boolean =>
  !cycle.end_date || cycle.end_date >= todayKey;
